// The High Peaks: the section east of the Northern Reach. An elevation
// field shapes it: alpine meadow in the valleys (grey-green grass, gentians
// and edelweiss, larch and spruce), scree and crags higher up, and snow on the
// heights, which join the Reach's snowfields along the shared edge. The
// mountains themselves are big upright sprites in ranges along the ridges, so
// trails wind between them through passes. Alpine lakes lie in the basins; the
// largest spills a river south. No places yet: this is land to wander.
//
// Its own random streams (seed + 600 and up), consumed in full whatever region
// is being painted, like the rest of the ground.
import { rng, noise2, softShadow, wash, blobPath, inkPoly, inkEllipse, line, withAlpha, mix, hatch } from "./paint"
import { paintTuft, paintBush } from "./nature"
import { paintSpruce, paintBoulder } from "./north"

export function buildPeaks(config, { x0, x1, y1, edges = {} }) {
    const seed = config.seed
    const active = x1 > x0 && !!config.sections?.some((s) => s.key === "peaks")
    const n = noise2(seed + 600), n2 = noise2(seed + 601), n3 = noise2(seed + 602)
    const w = x1 - x0, h = y1
    const lakes = (config.peaks_lakes || []).map((l, i) => ({ ...l, seed: seed + 610 + i }))
    const inLake = (x, y, pad = 0) => lakes.some((l) => ((x - l.x) / (l.rx + pad)) ** 2 + ((y - l.y) / (l.ry + pad)) ** 2 < 1)
    const coast = config.peaks_coast || []

    // 0 = the valley floor, 1 = a summit
    function elevation(x, y) {
        if (!active || x < x0 - 400) return 0
        x = Math.max(x, x0) // just west of the edge reads as the edge
        const ridge = 1 - Math.abs(n(x / 950, y / 950) * 2 - 1)
        const north = 1 - Math.min(1, y / 4800)
        const fine = n2(x / 320, y / 320)
        // low ground along the western shore, south of where the Reach's coast meets it
        const shore = y > 2300 ? Math.max(0, 1 - (x - x0 - 300) / 900) : 0
        let e = 0.12 + ridge * 0.45 + north * 0.3 + fine * 0.16 - shore * 0.4
        // the slopes by the Reach's snowfields stay snowy, so the two meet
        const seam = x - x0
        if (seam < 600 && y < 2400) e = Math.max(e, 0.7 - (seam / 600) * 0.08 - Math.max(0, y - 2000) / 1600)
        // the lakes lie in basins
        lakes.forEach((l) => { const d = Math.hypot((x - l.x) / (l.rx * 2.4), (y - l.y) / (l.ry * 2.4)); if (d < 1) e -= (1 - d) * 0.4 })
        return Math.max(0, Math.min(1, e))
    }
    const SNOW = 0.66, CRAG = 0.56
    function biome(x, y) {
        const e = elevation(x, y)
        return e > SNOW ? "peak-snow" : e > CRAG ? "crag" : "alpine"
    }
    const trailPals = new Map()
    function groundPal(pal, x, y) {
        if (biome(x, y) === "alpine") return pal
        if (!trailPals.has(pal)) trailPals.set(pal, { ...pal, path: pal.snowTrail, pathLight: pal.snowLight, pathDark: pal.snowTrailDark, pebble: pal.stoneLight })
        return trailPals.get(pal)
    }

    // --- the land -----------------------------------------------------------------

    const CELL = 105
    function paintLand(ctx, pal, vis) {
        if (!active) return
        const area = (w * h) / (2000 * 1250)
        ctx.save()
        ctx.beginPath(); ctx.rect(x0 - 2, -40, w + 42, h + 80); ctx.clip()
        // snow underneath (so the heights meet the Reach's snowfields without a
        // seam), then the lower ground laid over it by elevation: a blob per
        // cell, so the zones meet raggedly
        ctx.fillStyle = pal.snow; ctx.fillRect(x0 - 2, -40, w + 42, h + 80)
        // with land to the south, the valley floor runs on under its ragged edge
        if (!edges.bottom) { ctx.restore(); ctx.save(); ctx.beginPath(); ctx.rect(x0 - 2, -40, w + 42, h + 640); ctx.clip(); ctx.fillStyle = pal.alpine; ctx.fillRect(x0 - 2, h - 120, w + 42, 760) }
        const r = rng(seed + 603)
        for (let gy = -CELL; gy < h + CELL; gy += CELL) {
            for (let gx = x0 - CELL; gx < x1 + CELL; gx += CELL) {
                const x = gx + (r() - 0.5) * CELL * 0.6, y = gy + (r() - 0.5) * CELL * 0.6, rad = CELL * (0.95 + r() * 0.3), sd = Math.floor(r() * 1e6)
                if (!vis(x, y, rad + 10)) continue
                const e = elevation(x, y)
                if (e > SNOW) continue
                const color = e > CRAG + 0.06 ? pal.scree : e > CRAG ? mix(pal.alpine, pal.scree, 0.55) : pal.alpine
                blobPath(ctx, x, y, rad, rad * 0.8, sd, 0.22, 14)
                ctx.fillStyle = color; ctx.fill()
            }
        }
        // wind drifts on the snow: a bright crest over a blue shadow
        const rd = rng(seed + 608)
        for (let i = 0; i < 140 * area; i++) {
            const x = x0 + rd() * w, y = rd() * h, len = 40 + rd() * 110, bow = (rd() - 0.3) * 18, tilt = -0.25 + (rd() - 0.5) * 0.3
            if (!vis(x, y, len) || elevation(x, y) <= SNOW) continue
            const dx = Math.cos(tilt) * len, dy = Math.sin(tilt) * len
            ctx.lineCap = "round"
            ctx.strokeStyle = withAlpha(pal.snowDeep, 0.5); ctx.lineWidth = 2.2
            ctx.beginPath(); ctx.moveTo(x - dx / 2, y - dy / 2 + 2.5); ctx.quadraticCurveTo(x, y + bow + 2.5, x + dx / 2, y + dy / 2 + 2.5); ctx.stroke()
            ctx.strokeStyle = withAlpha(pal.snowLight, 0.9); ctx.lineWidth = 1.6
            ctx.beginPath(); ctx.moveTo(x - dx / 2, y - dy / 2); ctx.quadraticCurveTo(x, y + bow, x + dx / 2, y + dy / 2); ctx.stroke()
        }
        // soft washes: sunlit and shadowed slopes, greener hollows
        const rw = rng(seed + 604)
        for (let i = 0; i < 80 * area; i++) {
            const x = x0 + rw() * w, y = rw() * h, rad = 80 + rw() * 220, a = 0.3 + rw() * 0.3
            if (!vis(x, y, rad)) continue
            const e = elevation(x, y), v = n3(x / 260, y / 260)
            const color = e > SNOW ? (v > 0.5 ? pal.snowLight : pal.snowShade) : e > CRAG ? (v > 0.5 ? pal.screeLight : pal.screeDark) : (v > 0.55 ? pal.alpineLight : v > 0.35 ? pal.alpineDark : pal.groundDark)
            wash(ctx, x, y, rad, color, a * (e > CRAG ? 0.5 : 0.75))
        }
        // blue shadow pools and bright rises on the snow, as on the Reach's
        const rp = rng(seed + 609)
        for (let i = 0; i < 70 * area; i++) {
            const x = x0 + rp() * w, y = rp() * h, rad = 90 + rp() * 260, a = 0.35 + rp() * 0.3
            if (!vis(x, y, rad) || elevation(x, y) <= SNOW) continue
            const v = n2(x / 330 + 7, y / 330)
            wash(ctx, x, y, rad, v > 0.58 ? pal.snowLight : v > 0.42 ? pal.snowShade : pal.snowDeep, v > 0.58 ? a * 0.8 : a * 0.6)
        }
        // texture: grass dabs low, grit on the scree, sparkle on the snow
        for (let i = 0; i < 6400 * area; i++) {
            const x = x0 + rw() * w, y = rw() * h, kind = rw()
            if (!vis(x, y, 4)) continue
            const e = elevation(x, y)
            if (e > SNOW) { if (kind < 0.3) { ctx.fillStyle = withAlpha(kind < 0.1 ? pal.snowShade : pal.snowLight, 0.8); ctx.beginPath(); ctx.arc(x, y, 0.9, 0, Math.PI * 2); ctx.fill() } continue }
            if (e > CRAG) { ctx.fillStyle = withAlpha(kind < 0.5 ? pal.screeDark : pal.screeLight, 0.7); ctx.beginPath(); ctx.arc(x, y, 1 + kind * 1.4, 0, Math.PI * 2); ctx.fill(); continue }
            const v = n3(x / 40, y / 40)
            ctx.fillStyle = v > 0.6 ? withAlpha(pal.alpineLight, 0.55) : v < 0.35 ? withAlpha(pal.alpineDark, 0.45) : withAlpha(pal.alpine, 0.3)
            ctx.beginPath(); ctx.ellipse(x, y, 2.4, 1.3, 0, 0, Math.PI * 2); ctx.fill()
        }
        // hatching down the steep ground, the way an old map draws slopes
        const rh = rng(seed + 605)
        for (let i = 0; i < 1400 * area; i++) {
            const x = x0 + rh() * w, y = rh() * h, len = 6 + rh() * 8
            if (!vis(x, y, 16)) continue
            const e = elevation(x, y)
            if (e < CRAG - 0.04 || e > SNOW + 0.1) continue
            const gx = elevation(x + 30, y) - elevation(x - 30, y), gy = elevation(x, y + 30) - elevation(x, y - 30)
            const m = Math.hypot(gx, gy)
            if (m < 0.02) continue
            line(ctx, [[x, y], [x - (gx / m) * len, y - (gy / m) * len]], withAlpha(pal.cragDark, 0.45), 1)
        }
        // the edges of the world darken, blue on the snow
        const edge = 160
        const darken = [[x1, 0, x1 - edge, 0, x1 - edge, 0, edge, h], [0, 0, 0, edge, x0, 0, w, edge]]
        if (edges.bottom) darken.push([0, h, 0, h - edge, x0, h - edge, w, edge])
        darken.forEach(([gx0, gy0, gx1, gy1, rx, ry, rwid, rht]) => {
            const g = ctx.createLinearGradient(gx0, gy0, gx1, gy1)
            g.addColorStop(0, withAlpha(pal.cragDark, 0.55)); g.addColorStop(1, withAlpha(pal.cragDark, 0))
            ctx.fillStyle = g; ctx.fillRect(rx, ry, rwid, rht)
        })
        ctx.restore()
        // the seam with the Reach: shade that straddles it, so the two
        // snowfields read as one (unclipped, so it reaches both sides)
        const rs = rng(seed + 607)
        for (let y = -40; y < 2500; y += 70) {
            const x = x0 + (rs() - 0.5) * 140, rad = 130 + rs() * 110, a = 0.3 + rs() * 0.2, v = rs()
            if (!vis(x, y, rad)) continue
            wash(ctx, x, y, rad, v < 0.6 ? pal.snowShade : pal.snowLight, a)
        }
    }

    // A rocky shore where the Peaks meet the sea: boulders, not a beach.
    function paintShore(ctx, pal, vis) {
        if (!active || coast.length < 2) return
        const pts = coast.map(([x, y]) => ({ x, y }))
        if (!pts.some((p) => vis(p.x, p.y, 200))) return
        ctx.save()
        ctx.lineCap = "round"; ctx.lineJoin = "round"
        ;[[110, pal.screeDark], [60, pal.scree]].forEach(([width, color]) => {
            ctx.strokeStyle = color; ctx.lineWidth = width
            ctx.beginPath(); pts.forEach((p, i) => (i ? ctx.lineTo(p.x, p.y) : ctx.moveTo(p.x, p.y))); ctx.stroke()
        })
        ctx.restore()
    }

    // Alpine lakes: a rim of scree, then deep, clear, cold water.
    function paintLakes(ctx, pal, vis) {
        lakes.forEach((l) => {
            const r = rng(l.seed)
            const glints = Array.from({ length: 12 }, () => [r(), r(), r()])
            if (!vis(l.x, l.y, Math.max(l.rx, l.ry) + 60)) return
            blobPath(ctx, l.x, l.y + 4, l.rx + 22, l.ry + 16, l.seed, 0.06, 30)
            ctx.fillStyle = pal.scree; ctx.fill()
            blobPath(ctx, l.x, l.y, l.rx, l.ry, l.seed + 1, 0.05, 30)
            const g = ctx.createRadialGradient(l.x - l.rx * 0.2, l.y - l.ry * 0.2, 10, l.x, l.y, Math.max(l.rx, l.ry))
            g.addColorStop(0, pal.tarnDeep); g.addColorStop(0.75, pal.tarn); g.addColorStop(1, mix(pal.tarn, pal.waterLight, 0.4))
            ctx.fillStyle = g; ctx.fill()
            ctx.strokeStyle = pal.ink; ctx.lineWidth = 1.8; ctx.stroke()
            ctx.save()
            blobPath(ctx, l.x, l.y, l.rx, l.ry, l.seed + 1, 0.05, 30); ctx.clip()
            ctx.strokeStyle = withAlpha(pal.foam, 0.45); ctx.lineWidth = 4
            blobPath(ctx, l.x, l.y, l.rx - 6, l.ry - 5, l.seed + 2, 0.05, 30); ctx.stroke()
            // the mountains' reflection: pale, broken strokes
            ctx.strokeStyle = withAlpha(pal.snowLight, pal.night ? 0.12 : 0.3); ctx.lineWidth = 1.4
            glints.forEach(([a, b, c]) => {
                const x = l.x + (a - 0.5) * l.rx * 1.5, y = l.y + (b - 0.5) * l.ry * 1.4, len = 10 + c * 30
                ctx.beginPath(); ctx.moveTo(x - len / 2, y); ctx.lineTo(x + len / 2, y); ctx.stroke()
            })
            ctx.restore()
            // stones round the shore
            for (let i = 0; i < 26; i++) {
                const a = r() * Math.PI * 2, s = 3 + r() * 5
                inkEllipse(ctx, l.x + Math.cos(a) * (l.rx + 10), l.y + Math.sin(a) * (l.ry + 8), s, s * 0.7, r() < 0.5 ? pal.stone : pal.stoneLight, withAlpha(pal.ink, 0.6), 0.8)
            }
        })
    }

    // --- mountains and nature ------------------------------------------------------------

    let allMountains = []
    function grow({ clearOf, spaced, note, keep = () => true }) {
        const sprites = [], footprints = [], trees = [], smalls = [], mountains = []
        if (!active) return { sprites, footprints, trees, smalls, mountains }
        const r = rng(seed + 620)
        // mountains along the high ground, biggest where it's highest
        for (let gy = 140; gy < h - 100; gy += 250) {
            for (let gx = x0 + 160; gx < x1 - 120; gx += 250) {
                const x = gx + (r() - 0.5) * 180, y = gy + (r() - 0.5) * 160, roll = r(), sd = Math.floor(r() * 1e6)
                const e = elevation(x, y)
                if (e < 0.58 || roll > (e - 0.58) * 2.4) continue
                const s = Math.min(300, 110 + (e - 0.58) * 520 + roll * 70)
                // clear of trails by its whole foot (side lobes too), so a
                // range can never wall a trail off
                if (inLake(x, y, s + 60) || !clearOf(x, y, s * 0.95)) continue
                if (mountains.some((m) => Math.hypot(m.x - x, (m.y - y) * 1.4) < (m.s + s) * 0.78)) continue
                mountains.push({ x, y, s, seed: sd, snowLine: 0.25 + Math.max(0, 0.75 - e) * 0.6 })
            }
        }
        // `keep` clears what stands on a trail added since, without changing
        // what grew around it: the valleys still see every mountain
        const grown = mountains.slice()
        allMountains = grown
        for (let i = mountains.length - 1; i >= 0; i--) if (!keep({ ...mountains[i], kind: "mountain" })) mountains.splice(i, 1)
        mountains.forEach((m) => {
            const ht = m.s * (1.05 + (m.seed % 7) / 20)
            sprites.push({ x: m.x, y: m.y, box: [-m.s * 1.15, -ht * 1.1, m.s * 1.15, 12], kind: "mountain", sway: 0, paint(ctx, pal) { paintMountain(ctx, pal, m.s, ht, m.seed, m.snowLine) } })
            // the base blocks walking; you go round, through the passes
            footprints.push({ circle: true, x: m.x, y: m.y - m.s * 0.12, r: m.s * 0.55 })
            footprints.push({ circle: true, x: m.x - m.s * 0.5, y: m.y - m.s * 0.06, r: m.s * 0.32 })
            footprints.push({ circle: true, x: m.x + m.s * 0.5, y: m.y - m.s * 0.06, r: m.s * 0.32 })
        })
        const inMountain = (x, y, pad) => grown.some((m) => Math.hypot(m.x - x, (m.y - m.s * 0.1 - y) * 1.3) < m.s * 0.9 + pad)
        // spruce and stone pine in the valleys, thinning upward
        const grove = noise2(seed + 621)
        const target = Math.round(170 * (w * h * 0.5) / (2000 * 1250) * 0.95)
        for (let i = 0; i < target * 16 && trees.length < target; i++) {
            const x = x0 + 40 + r() * (w - 80), y = 40 + r() * (h - 80), g = grove(x / 360, y / 360), size0 = r(), roll = r(), sd = Math.floor(r() * 1e6)
            const e = elevation(x, y)
            // by the Reach, snowy spruce carry on across the edge and thin out
            const seamWood = Math.max(0, 1 - (x - x0) / 900) * (y < 2500 ? 1 : 0)
            const high = e > CRAG + 0.04
            if (high ? g < 1.02 - seamWood * 0.55 : g < 0.42 + e * 0.3) continue
            if (inLake(x, y, 40) || inMountain(x, y, 20)) continue
            const s = 16 + size0 * 12
            if (!clearOf(x, y, s * 0.6) || !spaced(x, y, s)) continue
            const t = { x, y, s, kind: roll < 0.7 ? "spruce" : "larch", snowy: e > CRAG - 0.05, seed: sd }
            trees.push(t); note(t)
        }
        for (let i = trees.length - 1; i >= 0; i--) if (!keep(trees[i])) trees.splice(i, 1)
        trees.forEach((t) => {
            if (t.kind === "larch") sprites.push({ x: t.x, y: t.y, box: [-t.s * 1.05, -t.s * 3.9, t.s * 1.05, 6], kind: "tree", sway: 0.7, seed: t.seed, paint(ctx, pal) { paintSpruce(ctx, larchPal(pal), t.s, t.seed, false) } })
            else sprites.push({ x: t.x, y: t.y, box: [-t.s * 1.05, -t.s * 3.9, t.s * 1.05, 6], kind: "tree", sway: 0.55, seed: t.seed, paint(ctx, pal) { paintSpruce(ctx, pal, t.s, t.seed, t.snowy) } })
            footprints.push({ circle: true, x: t.x, y: t.y - 2, r: t.s * 0.26 + 4 })
        })
        // boulders on the scree and in the meadows
        for (let i = 0, made = 0; i < 1400 && made < Math.round(22 * (w * h) / (2000 * 1250) * 0.5); i++) {
            const x = x0 + 40 + r() * (w - 80), y = 40 + r() * (h - 80), s = 9 + r() * 16, sd = Math.floor(r() * 1e6)
            const e = elevation(x, y)
            if (e > SNOW + 0.08 || inLake(x, y, 30) || inMountain(x, y, 10) || !clearOf(x, y, 8) || !spaced(x, y, s)) continue
            const o = { x, y, s, snowy: e > SNOW - 0.04, seed: sd }
            note(o); made++
            if (!keep(o)) continue
            smalls.push(o)
            sprites.push({ x, y, box: [-s * 1.4, -s * 1.3, s * 1.4, s * 0.8 + 4], kind: "rock", sway: 0, paint(ctx, pal) { paintBoulder(ctx, pal, s, sd, o.snowy) } })
            footprints.push({ circle: true, x, y: y - 2, r: s * 0.8 })
        }
        // dwarf pine: low dark bushes across the meadows and up into the scree
        for (let i = 0, made = 0; i < 2400 && made < Math.round(30 * (w * h) / (2000 * 1250)); i++) {
            const x = x0 + 40 + r() * (w - 80), y = 40 + r() * (h - 80), s = 10 + r() * 7, sd = Math.floor(r() * 1e6)
            const e = elevation(x, y)
            if (e > CRAG + 0.08 || inLake(x, y, 30) || inMountain(x, y, 10) || !clearOf(x, y, 8) || !spaced(x, y, s)) continue
            const o = { x, y, s, seed: sd }
            note(o); made++
            if (!keep(o)) continue
            smalls.push(o)
            sprites.push({ x, y, box: [-s * 1.7, -s * 2.2, s * 1.7, s * 0.8 + 4], kind: "bush", sway: 0.3, paint(ctx, pal) { paintBush(ctx, dwarfPinePal(pal), s, sd) } })
        }
        return { sprites, footprints, trees, smalls, mountains }
    }

    // The valley floor above the Wildwood, once the map's edge: groves and
    // dwarf pine from their own stream. Grown after every land (world.js
    // calls it last), so no tree anywhere else sees these and moves.
    function growValley({ clearOf, spaced, note, keep = () => true }) {
        const sprites = [], footprints = [], trees = [], smalls = []
        if (!active || edges.bottom) return { sprites, footprints, trees, smalls }
        const grove = noise2(seed + 621)
        const inMountain = (x, y, pad) => allMountains.some((m) => Math.hypot(m.x - x, (m.y - m.s * 0.1 - y) * 1.3) < m.s * 0.9 + pad)
        const rv = rng(seed + 640)
        const want = Math.round(70 * (w / 4000))
        for (let i = 0, made = 0; i < want * 14 && made < want; i++) {
            const x = x0 + 60 + rv() * (w - 120), y = h - 1100 + rv() * 1000, g = grove(x / 300, y / 300), roll = rv(), size0 = rv(), sd = Math.floor(rv() * 1e6)
            const e = elevation(x, y)
            if (g < 0.5 || e > CRAG || inLake(x, y, 40) || inMountain(x, y, 20)) continue
            const bush = roll > 0.72, s = bush ? 10 + size0 * 7 : 16 + size0 * 12
            if (!clearOf(x, y, s * 0.6) || !spaced(x, y, s)) continue
            const o = bush ? { x, y, s, seed: sd } : { x, y, s, kind: roll < 0.45 ? "spruce" : "larch", snowy: false, seed: sd }
            note(o); made++
            if (!keep(o)) continue
            if (bush) {
                smalls.push(o)
                sprites.push({ x, y, box: [-s * 1.7, -s * 2.2, s * 1.7, s * 0.8 + 4], kind: "bush", sway: 0.3, paint(ctx, pal) { paintBush(ctx, dwarfPinePal(pal), s, sd) } })
            } else {
                trees.push(o)
                const painter = o.kind === "larch" ? (ctx, pal) => paintSpruce(ctx, larchPal(pal), s, sd, false) : (ctx, pal) => paintSpruce(ctx, pal, s, sd, false)
                sprites.push({ x, y, box: [-s * 1.05, -s * 3.9, s * 1.05, 6], kind: "tree", sway: o.kind === "larch" ? 0.7 : 0.55, seed: sd, paint: painter })
                footprints.push({ circle: true, x, y: y - 2, r: s * 0.26 + 4 })
            }
        }
        return { sprites, footprints, trees, smalls }
    }

    function paintShadows(ctx, pal, vis, grown) {
        grown.mountains.forEach((m) => {
            if (!vis(m.x, m.y, m.s * 1.6)) return
            // a scree apron the mountain rises out of, then its shadow
            ctx.save(); ctx.translate(m.x, m.y - 2); ctx.scale(1, 0.28)
            wash(ctx, 0, 0, m.s * 1.25, pal.scree, 0.85)
            wash(ctx, m.s * 0.2, 0, m.s * 0.9, pal.screeDark, 0.35)
            ctx.restore()
            softShadow(ctx, m.x + m.s * 0.35, m.y + 6, m.s * 1.25, m.s * 0.32, pal.shadowAlpha * 1.2, pal.shadow)
        })
        grown.trees.forEach((t) => { if (vis(t.x, t.y, t.s * 2)) softShadow(ctx, t.x + t.s * 0.45, t.y + 2, t.s * 1.15, t.s * 0.45, pal.shadowAlpha, pal.shadow) })
        grown.smalls.forEach((o) => { if (vis(o.x, o.y, o.s * 2)) softShadow(ctx, o.x + o.s * 0.3, o.y + 1, o.s * 1.2, o.s * 0.45, pal.shadowAlpha * 0.8, pal.shadow) })
    }

    // Gentians and edelweiss in the meadows, and grass tufts.
    function paintDetails(ctx, pal, vis, clearOf, inSea) {
        if (!active) return
        const area = (w * h) / (2000 * 1250)
        const r = rng(seed + 606)
        for (let i = 0; i < 130 * area; i++) {
            const x = x0 + r() * w, y = r() * h, kind = r(), sd = Math.floor(r() * 1e6)
            if (!vis(x, y, 20)) continue
            if (elevation(x, y) > CRAG - 0.05 || inLake(x, y, 20) || inSea(x, y, 60) || !clearOf(x, y, -10)) continue
            alpineFlowers(ctx, pal, x, y, sd, kind < 0.55 ? pal.gentian : pal.edelweiss)
        }
        // stones on the scree: crisp, inked, in little spills
        for (let i = 0; i < 420 * area; i++) {
            const x = x0 + r() * w, y = r() * h, s = 2 + r() * 5, light = r() < 0.5, n0 = 1 + Math.floor(r() * 3)
            if (!vis(x, y, 20)) continue
            const e = elevation(x, y)
            if (e < CRAG - 0.02 || e > SNOW + 0.02 || inLake(x, y, 10) || inSea(x, y, 40)) continue
            for (let k = 0; k < n0; k++) inkEllipse(ctx, x + k * s * 1.4, y + (k % 2) * s * 0.6, s, s * 0.62, light ? pal.screeLight : pal.scree, withAlpha(pal.ink, 0.55), 0.8)
        }
        ctx.lineWidth = 1.1
        for (let i = 0; i < 3000 * area; i++) {
            const x = x0 + r() * w, y = r() * h, ht = 4 + r() * 4
            if (!vis(x, y, 8)) continue
            const e = elevation(x, y)
            if (e > CRAG || inLake(x, y, 6) || inSea(x, y, 60) || !clearOf(x, y, -14)) continue
            ctx.globalAlpha = 0.35 + n3(x / 90, y / 90) * 0.4
            paintTuft(ctx, n3(x / 50, y / 50) > 0.5 ? pal.alpineLight : pal.alpineDark, x, y, ht)
        }
        ctx.globalAlpha = 1
    }

    return { active, lakes, inLake, elevation, biome, groundPal, paintLand, paintShore, paintLakes, grow, growValley, paintShadows, paintDetails }
}

const dwarfCache = new Map()
function dwarfPinePal(pal) {
    if (!dwarfCache.has(pal)) dwarfCache.set(pal, { ...pal, leaf: pal.spruceLight, leafDark: pal.spruceDark, leafLight: mix(pal.spruceLight, pal.alpineLight, 0.4), berry: pal.spruceDark, flowers: [pal.spruceDark] })
    return dwarfCache.get(pal)
}

const larchCache = new Map()
function larchPal(pal) {
    if (!larchCache.has(pal)) larchCache.set(pal, { ...pal, spruce: pal.birchLeafDark, spruceLight: pal.birchLeaf, spruceDark: mix(pal.birchLeafDark, "#000000", 0.2) })
    return larchCache.get(pal)
}

// A mountain, drawn upright like everything else on the map: a jagged
// ridgeline (sometimes with a second, lower summit), each summit's sunlit face
// to the upper left and a hatched spine of shade down its right, couloirs and
// ledges, snow from the summits down to a ragged snowline, and no ink along
// the foot, so it rises out of the ground rather than sitting on it.
export function paintMountain(ctx, pal, s, h, seed, snowLine = 0.35) {
    const r = rng(seed)
    const ink = pal.ink
    const summits = [{ x: (r() - 0.5) * s * 0.4, h }]
    if (r() < 0.55) summits.push({ x: summits[0].x + (r() < 0.5 ? -1 : 1) * s * (0.45 + r() * 0.2), h: h * (0.55 + r() * 0.2) })
    // the ridgeline: the highest of the summits' slopes at each x, roughened
    const steps = 22
    const ridge = []
    for (let i = 0; i <= steps; i++) {
        const t = i / steps, x = -s + t * s * 2
        let y = 0
        summits.forEach((p) => { const reach = p === summits[0] ? s * 1.02 : s * 0.62; y = Math.max(y, p.h * Math.max(0, 1 - Math.abs(x - p.x) / reach)) })
        const rough = (r() - 0.5) * h * 0.07 * (0.4 + y / h)
        ridge.push([x, -Math.max(0, y + (i === 0 || i === steps ? 0 : rough))])
    }
    ridge[0] = [-s, 0]; ridge[steps] = [s, 0]
    summits.forEach((p) => { const k = Math.round(((p.x + s) / (s * 2)) * steps); if (k > 0 && k < steps) ridge[k] = [p.x, -p.h] })
    const trace = () => { ctx.beginPath(); ridge.forEach(([x, y], i) => (i ? ctx.lineTo(x, y) : ctx.moveTo(x, y))); ctx.closePath() }
    trace()
    const g = ctx.createLinearGradient(-s, -h, s * 0.4, 0)
    g.addColorStop(0, pal.cragLight); g.addColorStop(0.55, pal.crag); g.addColorStop(1, mix(pal.crag, pal.cragDark, 0.3))
    ctx.fillStyle = g; ctx.fill()
    ctx.save()
    trace(); ctx.clip()
    summits.forEach((p, pi) => {
        // the shaded side: a crooked spine from the summit down and to the right
        const spine = [[p.x, -p.h]]
        for (let i = 1; i <= 5; i++) spine.push([p.x + s * 0.09 * i + (r() - 0.5) * s * 0.08, -p.h + (p.h * i) / 5])
        ctx.fillStyle = pal.cragDark
        ctx.beginPath(); spine.forEach(([x, y], i) => (i ? ctx.lineTo(x, y) : ctx.moveTo(x, y))); ctx.lineTo(p.x + s * 1.3, 0); ctx.lineTo(p.x + s * 1.3, -p.h * 1.2); ctx.closePath(); ctx.fill()
        hatch(ctx, [p.x, -p.h, s * 1.3, p.h], mix(pal.cragDark, "#000000", 0.28), { seed: seed + pi, angle: -1.1, gap: 3.2, alpha: 0.55 })
        // couloirs: dark gullies down the shaded face
        for (let c = 0; c < 3; c++) {
            const sx = p.x + s * (0.1 + r() * 0.35), sy = -p.h * (0.7 + r() * 0.2)
            line(ctx, [[sx, sy], [sx + s * 0.06, sy + p.h * 0.25], [sx + s * 0.04 + (r() - 0.5) * s * 0.05, sy + p.h * 0.5]], withAlpha(mix(pal.cragDark, "#000000", 0.35), 0.8), 1.6)
        }
        // ledges across the lit face
        for (let l = 0; l < 7; l++) {
            const t = 0.2 + r() * 0.7, x = p.x - s * (0.15 + r() * 0.6) * t, y = -p.h * (1 - t)
            line(ctx, [[x - s * 0.06, y], [x + s * 0.07, y + p.h * 0.015]], withAlpha(ink, 0.3), 1)
        }
        // snow from the summit to its snowline, ragged at the hem, blue on the shaded side
        const hem = -p.h * (1 - snowLine)
        const half = s * (1 - snowLine) * (p === summits[0] ? 1.02 : 0.62)
        ctx.fillStyle = pal.snowLight
        ctx.beginPath(); ctx.moveTo(p.x, -p.h - 1)
        const across = 9
        for (let i = 0; i <= across; i++) {
            const t = i / across, x = p.x - half + t * half * 2
            const drip = (i % 2 ? 1 : -0.35) * p.h * 0.07 * (0.4 + r())
            ctx.lineTo(x, hem + drip + Math.abs(t - 0.5) * p.h * 0.1)
        }
        ctx.closePath(); ctx.fill()
        ctx.fillStyle = withAlpha(pal.snowShade, 0.95)
        ctx.beginPath(); ctx.moveTo(p.x, -p.h); spine.slice(0, 4).forEach(([x, y]) => ctx.lineTo(x, Math.min(y, hem))); ctx.lineTo(p.x + half * 0.9, hem + p.h * 0.02); ctx.closePath(); ctx.fill()
        // a streak of wind-blown snow off the summit
        line(ctx, [[p.x, -p.h], [p.x - s * 0.18, -p.h * 0.84]], withAlpha(pal.snowLight, 0.9), 2)
    })
    ctx.restore()
    // ink along the ridgeline only: the foot has no outline
    ctx.beginPath(); ridge.slice(1, steps).forEach(([x, y], i) => (i ? ctx.lineTo(x, y) : ctx.moveTo(x, y)))
    ctx.strokeStyle = ink; ctx.lineWidth = 1.9; ctx.lineJoin = "round"; ctx.lineCap = "round"; ctx.stroke()
    // scree fanning out at the foot
    for (let i = 0; i < 16; i++) {
        const x = (r() - 0.5) * s * 1.9, y = -r() * 10
        inkEllipse(ctx, x, y, 3 + r() * 5, 2 + r() * 2.4, r() < 0.5 ? pal.scree : pal.screeLight, withAlpha(ink, 0.5), 0.8)
    }
}

function alpineFlowers(ctx, pal, x, y, seed, color) {
    const r = rng(seed)
    for (let i = 0; i < 6; i++) {
        const fx = x + (r() - 0.5) * 18, fy = y + (r() - 0.5) * 9
        line(ctx, [[fx, fy + 4], [fx, fy]], pal.alpineDark, 1)
        ctx.fillStyle = color
        for (let p = 0; p < 5; p++) { const a = (p / 5) * Math.PI * 2; ctx.beginPath(); ctx.arc(fx + Math.cos(a) * 1.7, fy + Math.sin(a) * 1.7, 1.3, 0, Math.PI * 2); ctx.fill() }
        ctx.fillStyle = "#F2C14E"; ctx.beginPath(); ctx.arc(fx, fy, 0.8, 0, Math.PI * 2); ctx.fill()
    }
}
